import React, { useContext, useEffect, useState } from "react";
import translate from "../i18n/Translator";
import DialogPopup from "../components/DialogPopup";
import { Button, Grid, Hidden } from "@material-ui/core";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import UploaderInput from "../components/UploaderInput";
import { AppContext } from "../context/AppContext";
import { ErrorSnackbar } from "../components/Snackbars";
import { RegisterPaymentRequest, PAYMENT_FORM } from "../model/Payment";
import { registerPayment } from "../api/CustomerPaymentAPI";
import DatePicker from "../components/DatePicker";
import moment from "moment";
import { CURRENCIES } from "../model/Currency";
import { GTZeroValidator } from "../components/Validators";
import { Redirect } from "react-router-dom";

interface RegisterPaymentPopupProps {
    onClose(): any;
}

export default function RegisterPaymentPopup(props: RegisterPaymentPopupProps) {
    const context = useContext(AppContext);
    const paymentFormLabels = PAYMENT_FORM.map(form => translate(`payments.form.${form}`) as string);
    const gtzValidator = new GTZeroValidator();

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>();
    const [request, setRequest] = useState<RegisterPaymentRequest>({} as RegisterPaymentRequest);
    const [currencies, setCurrencies] = useState<string[]>([]);
    const [currencyLabels, setCurrencyLabels] = useState<string[]>([]);
    const [validations, setValidations] = useState<any>({} as any);
    const [redirect, setRedirect] = useState<string>();

    useEffect(() => {
        let customerCurrencies = context.session?.customer?.currencies;
        if (!customerCurrencies || customerCurrencies.length === 0) {
            customerCurrencies = CURRENCIES;
        }

        setCurrencies(customerCurrencies);
        setCurrencyLabels(customerCurrencies.map(currency => translate(`currencies.${currency}`) as string));

        setRequest({
            date: new Date(),
            currency: customerCurrencies[0],
            amount: 0
        } as RegisterPaymentRequest);
        // eslint-disable-next-line
    }, []);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const hasChangedNumber = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: +value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const onChangedDate = (name: string, raw?: string, date?: Date) => {
        if (date) {
            setRequest({ ...request, date: date })
        } else {
            setRequest({ ...request, date: new Date() })
        }
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onConfirm = () => {
        if (!isValid()) {
            return;
        }

        setSubmitting(true);
        registerPayment(context.session!.tenant!.id, context.session!.customer!.id, request).then(response => {
            setRedirect(`/payments/${response.id}`);
        }).catch(error => {
            setSubmitting(false);
            setError(error.message);
        });
    };

    if (redirect) {
        return (
            <Redirect to={redirect} />
        )
    }

    return (
        <DialogPopup open maxWidth="sm"
            title={translate("payments.register.title") as string}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            disable={submitting}
            onClose={props.onClose}
            closeColor="default"
            closeText={translate("buttons.cancel") as string}
            button={context.isGranted("CustomersPaymentsUpdate") ?
                <Button onClick={onConfirm} variant="outlined" color="primary" disabled={submitting}>
                    {translate("buttons.register_payment")}
                </Button>
                : undefined}>
            <Grid container justify="space-between" alignItems="center">
                <Grid item xs={12} className="DatePicker">
                    <DatePicker label={translate("payments.register.date") as string}
                        name="date" initial={request.date ? moment(request.date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")} onChange={onChangedDate}
                        defaultIfMissing={false} clearable={false} autoOK disableFuture />
                </Grid>
                <Grid item xs={12}>
                    <ValidatedInput type="text" id="currency" name="currency"
                        value={request.currency} label={translate("currencies.title") as string}
                        options={currencies} optionLabels={currencyLabels}
                        required disabled={currencies.length === 1 || submitting}
                        margin="dense"
                        onValueChanged={hasChanged} />
                </Grid>
                <Grid item xs={12}>
                    <ValidatedInput type="number" id="amount" name="amount"
                        value={request.amount ? request.amount.toString() : ""} label={translate("payments.register.amount") as string}
                        required validator={gtzValidator}
                        margin="dense"
                        onValueChanged={hasChangedNumber} />
                </Grid>
                <Grid item xs={12}>
                    <ValidatedInput type="text" id="form" name="form"
                        value={request.form} label={translate("payments.form.title") as string}
                        options={PAYMENT_FORM} optionLabels={paymentFormLabels}
                        required disabled={submitting}
                        margin="dense"
                        onValueChanged={hasChanged} />
                </Grid>
                <Grid item xs={12}>
                    <Hidden xsUp={request.form !== "CHECK"} implementation="css">
                        <ValidatedInput type="text" id="check_number" name="check_number"
                            value={request.check_number} label={translate("payments.register.check_number") as string}
                            required={request.form === "CHECK"} disabled={submitting}
                            margin="dense"
                            onValueChanged={hasChanged} />
                    </Hidden>
                </Grid>
                <Grid item xs={12}>
                    <Hidden xsUp={request.form !== "TRANSFER"} implementation="css">
                        <ValidatedInput type="text" id="reference" name="reference"
                            value={request.reference} label={translate("payments.register.reference") as string}
                            required={request.form === "TRANSFER"} disabled={submitting}
                            margin="dense"
                            onValueChanged={hasChanged} />
                    </Hidden>
                </Grid>
                <Grid item xs={12}>
                    <UploaderInput id="file" name="file"
                        value={request.file} label={translate("payments.register.file") as string}
                        required disabled={submitting}
                        margin="dense"
                        acceptExtension=".pdf,.png,.jpg"
                        onValueChanged={hasChanged} />
                </Grid>
            </Grid>
            <ErrorSnackbar message={error} onClose={() => setError(undefined)} />
        </DialogPopup>
    );

}